import * as QueryString from 'query-string';
import {matchPath} from 'react-router';
import {
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
} from '../routeData';

export function modifyQueryString(
  modFn: (orig: QueryString.ParsedQuery) => QueryString.ParsedQuery
) {
  const parsed = QueryString.parse(window.location.search) || {};
  const modded = modFn(parsed);
  window.history.replaceState(
    null,
    document.title,
    window.location.pathname + '?' + QueryString.stringify(modded)
  );
}

export function removeUrlProtocolPrefix(s: string) {
  return s.replace(/(^\w+:|^)\/\//, '');
}

export function parseNameAndIDFromURL(url: string): {id: string; name: string} {
  try {
    const nameAndID = url
      .split('?')[0] // remove querystring
      .replace(/\/$/, '') // remove trailing slash
      .split('/') // get last portion of URL
      .slice(-1)[0];
    return parseNameAndID(nameAndID);
  } catch (e) {
    throw new Error('Attempted to parse invalid URL');
  }
}

// Assumes s is <name>--<id> where <id> is a base64 encoded string
export function parseNameAndID(s: string): {id: string; name: string} {
  let id = s;
  let name = '';
  const sepPos = s.lastIndexOf('--');
  if (sepPos !== -1) {
    name = decodeURIComponentSafe(s.slice(0, sepPos)).replace(/-/g, ' ');
    id = s.slice(sepPos + 2, s.length);
  }

  // Re-add base64 padding
  const pad = 4 - (id.length % 4);
  if (pad !== 4) {
    for (let i = 0; i < pad; i++) {
      id += '=';
    }
  }

  const decoded = atob(id);
  if (decoded.indexOf(':') === -1) {
    throw new Error('Attempted to parse invalid View ID');
  }
  const [viewString, actualId] = decoded.split(':', 2);
  if (viewString !== 'View' || !actualId.match(/^-{0,1}\d+$/)) {
    throw new Error('Attempted to parse invalid View ID');
  }
  return {name, id};
}

// Assumes id is a base64 encoded string
export function makeNameAndID(id: string, name?: string): string {
  // Note we strip base64 = padding to make this pretty. It's added back
  // in parseNameAndID above.
  id = id.replace(/=/g, '');
  if (name != null) {
    // Replace all non word characters with dashes, eliminate repeating dashes
    name = name.replace(/\W/g, '-').replace(/-+/g, '-');
  }
  return name != null ? `${encodeURIComponent(name)}--${id}` : id;
}

export function urlSafeSlug(slug: string) {
  // Legacy urls have encoded components that google doesn't like, this makes
  // old urls use the same logic as makeNameAndID
  const parts = slug.split('--');
  return (
    parts[0].replace(/%[A-Z0-9]{2}/g, '-').replace(/-+/g, '-') + '--' + parts[1]
  );
}

export function parseRunTabPath(pathStr?: string) {
  return pathStr == null || pathStr === ''
    ? []
    : pathStr.split('/').map(p => decodeURIComponent(p));
}

// keep in sync with functions/src/meta.ts
export function canonicalize(url: string): string {
  let canonical = url.split('?')[0];
  if (canonical.endsWith('/')) {
    canonical = canonical.substring(0, canonical.length - 1);
  }
  return canonical;
}

export function decodeURIComponentSafe(str: string): string {
  try {
    return decodeURIComponent(str);
  } catch {
    return str;
  }
}

export function isOnReportView(pathname = window.location.pathname): boolean {
  const match = matchPath(pathname, {
    path: REPORT_PAGE_VIEW_PATH,
  });
  if (match != null) {
    return true;
  }
  const publishedMatch = matchPath(pathname, {
    path: REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
  });
  if (publishedMatch != null) {
    return true;
  }
  return false;
}

export function queryStringParamExists(param: string): boolean {
  const qs = QueryString.parse(window.location.search);
  return param in qs;
}

export function getQueryStringParamValue(
  param: string
): string | null | undefined {
  const qs = QueryString.parse(window.location.search);
  const res = qs[param];
  return Array.isArray(res) ? res[0] : res;
}
